import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import AdminShell from "./AdminShell";
import { firestore, storage } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ---------- UI (nya admin-stilen) ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1.1fr 0.9fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
`;

const Title = styled.h3`
  margin: 0;
  color: #111827;
`;

const Label = styled.div`
  font-weight: 900;
  margin: 10px 0 6px;
  color: #111827;
`;

const Input = styled.input`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
`;

const Textarea = styled.textarea`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
  min-height: 110px;
  resize: vertical;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 12px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const Danger = styled(Btn)`
  background: rgba(233, 78, 27, 0.08);
  color: #e94e1b;
  border: 1px solid rgba(233, 78, 27, 0.28);
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
  line-height: 1.45;
`;

const PillRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const Pill = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: ${(p) => (p.$active ? "rgba(15,118,110,0.12)" : "#fff")};
  color: ${(p) => (p.$active ? "#0f766e" : "#111827")};
  font-weight: 950;
  border-radius: 999px;
  padding: 9px 12px;
  cursor: pointer;

  &:hover {
    background: ${(p) => (p.$active ? "rgba(15,118,110,0.12)" : "#f9fafb")};
  }
`;

const FolderItem = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
`;

const FolderName = styled.div`
  font-weight: 950;
  color: #111827;
`;

const ThumbGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
`;

const Thumb = styled.img`
  width: 100%;
  height: 96px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #000;
`;

const ThumbWrap = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 10px;
  display: grid;
  gap: 8px;
`;

const ThumbActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
`;

const Tiny = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: #6b7280;
`;

/* ---------- Helpers ---------- */

function makeImagePath(folder, file) {
  const safeFolder = String(folder || "gallery")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-_]/g, "");

  const safeName = (file?.name || "image")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9.\-_]/g, "");

  return `gallery/${safeFolder}/${Date.now()}-${safeName}`;
}

function normalizeLinks(text) {
  return (text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

/* ---------- Component ---------- */

export default function AdminGallery() {
  const [busy, setBusy] = useState(false);

  // mode
  const [mode, setMode] = useState("image"); // "image" | "video"

  // form
  const [folder, setFolder] = useState("");
  const [files, setFiles] = useState([]);
  const [videoLinks, setVideoLinks] = useState("");

  // data
  const [folders, setFolders] = useState([]); // ["sommar", "tävling", ...]
  const [selectedFolder, setSelectedFolder] = useState("");
  const [items, setItems] = useState([]); // images/videos in selected folder

  const canUpload = useMemo(() => {
    if (!folder.trim()) return false;
    if (mode === "image") return files && files.length > 0;
    return normalizeLinks(videoLinks).length > 0;
  }, [folder, files, mode, videoLinks]);

  const loadFolders = async () => {
    const snap = await getDocs(collection(firestore, "gallery"));
    const set = new Set();
    snap.docs.forEach((d) => {
      const f = d.data()?.folder;
      if (f) set.add(f);
    });
    const list = Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
    setFolders(list);

    // auto-select om inget valt
    if (!selectedFolder && list.length) setSelectedFolder(list[0]);
  };

  const loadFolderItems = async (folderName) => {
    if (!folderName) {
      setItems([]);
      return;
    }

    const q = query(collection(firestore, "gallery"), where("folder", "==", folderName));
    const snap = await getDocs(q);
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // newest first (createdAt om finns)
    list.sort((a, b) => {
      const at = a.createdAt?.toMillis?.() || 0;
      const bt = b.createdAt?.toMillis?.() || 0;
      if (bt !== at) return bt - at;
      return String(b.id).localeCompare(String(a.id));
    });

    setItems(list);
  };

  useEffect(() => {
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadFolderItems(selectedFolder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder]);

  const resetForm = () => {
    setFolder("");
    setFiles([]);
    setVideoLinks("");
    setMode("image");
  };

  const uploadImages = async () => {
    const list = Array.from(files || []);
    if (!list.length) return;

    // upload i serie (snällare) – kan göras parallellt om du vill
    for (const file of list) {
      const storagePath = makeImagePath(folder, file);
      const r = ref(storage, storagePath);

      await uploadBytes(r, file);
      const url = await getDownloadURL(r);

      await addDoc(collection(firestore, "gallery"), {
        folder: folder.trim(),
        type: "image",
        url, // ✅ samma som din gamla frontend
        imageUrl: url, // ✅ extra
        title: file.name || null,
        storagePath, // ✅ viktigt för delete
        createdAt: serverTimestamp(),
      });
    }
  };

  const addVideos = async () => {
    const links = normalizeLinks(videoLinks);
    for (const link of links) {
      await addDoc(collection(firestore, "gallery"), {
        folder: folder.trim(),
        type: "video",
        youtubeLink: link,
        createdAt: serverTimestamp(),
      });
    }
  };

  const handleUpload = async () => {
    if (!canUpload) return;

    setBusy(true);
    try {
      if (mode === "image") await uploadImages();
      else await addVideos();

      alert("Klart!");
      await loadFolders();
      setSelectedFolder(folder.trim()); // hoppa direkt till nya mappen
      resetForm();
      await loadFolderItems(folder.trim());
    } catch (e) {
      alert(e?.message || "Uppladdning misslyckades.");
    } finally {
      setBusy(false);
    }
  };

  const deleteImage = async (it) => {
    if (!window.confirm("Ta bort denna bild?")) return;

    setBusy(true);
    try {
      // radera storage först
      if (it.storagePath) {
        await deleteObject(ref(storage, it.storagePath));
      } else if (it.url) {
        // fallback: om gammal data saknar storagePath – kan ibland faila men vi försöker
        try {
          await deleteObject(ref(storage, it.url));
        } catch {
          // ignore
        }
      }

      await deleteDoc(doc(firestore, "gallery", it.id));
      await loadFolderItems(selectedFolder);
    } catch (e) {
      alert(e?.message || "Kunde inte ta bort.");
    } finally {
      setBusy(false);
    }
  };

  const deleteFolder = async (folderName) => {
    if (!window.confirm(`Ta bort hela mappen "${folderName}" och allt innehåll?`)) return;

    setBusy(true);
    try {
      const q = query(collection(firestore, "gallery"), where("folder", "==", folderName));
      const snap = await getDocs(q);

      // radera filer + docs
      for (const d of snap.docs) {
        const data = d.data();
        if (data?.storagePath) {
          try {
            await deleteObject(ref(storage, data.storagePath));
          } catch {
            // ignore
          }
        }
        await deleteDoc(doc(firestore, "gallery", d.id));
      }

      alert("Mappen är borttagen.");
      await loadFolders();

      // välj annan
      if (selectedFolder === folderName) {
        setSelectedFolder("");
        setItems([]);
      }
    } catch (e) {
      alert(e?.message || "Kunde inte ta bort mappen.");
    } finally {
      setBusy(false);
    }
  };

  const imagesOnly = useMemo(() => items.filter((x) => x.type === "image"), [items]);
  const videosOnly = useMemo(() => items.filter((x) => x.type === "video"), [items]);

  return (
    <AdminShell title="Galleri (upload)">
      <Grid>
        {/* LEFT: skapa */}
        <Card>
          <Title>Lägg till i galleri</Title>

          <PillRow>
            <Pill type="button" $active={mode === "image"} onClick={() => setMode("image")}>
              🖼️ Bilder
            </Pill>
            <Pill type="button" $active={mode === "video"} onClick={() => setMode("video")}>
              ▶️ Video
            </Pill>
          </PillRow>

          <Label>Mappnamn</Label>
          <Input
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            placeholder="t.ex. Tävling 2026"
          />

          {mode === "image" ? (
            <>
              <Label>Välj bilder (flera)</Label>
              <Input type="file" accept="image/*" multiple onChange={(e) => setFiles(e.target.files || [])} />
              <Muted style={{ marginTop: 8 }}>
                Tips: vi sparar också <b>storagePath</b> så borttagning blir 100% korrekt.
              </Muted>
            </>
          ) : (
            <>
              <Label>YouTube-länkar (en per rad)</Label>
              <Textarea
                value={videoLinks}
                onChange={(e) => setVideoLinks(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </>
          )}

          <Row>
            <Btn type="button" onClick={handleUpload} disabled={busy || !canUpload}>
              {busy ? "Jobbar…" : mode === "image" ? "Ladda upp" : "Lägg till"}
            </Btn>
            <Ghost type="button" onClick={resetForm} disabled={busy}>
              Rensa
            </Ghost>
          </Row>

          <Muted style={{ marginTop: 12 }}>
            Sparas i <code>gallery</code> som: <b>folder</b>, <b>type</b>, <b>url/imageUrl</b>, <b>youtubeLink</b>,{" "}
            <b>storagePath</b>, <b>createdAt</b>.
          </Muted>
        </Card>

        {/* RIGHT: mappar + innehåll */}
        <Card>
          <Title>Mappar</Title>
          <div style={{ height: 10 }} />

          {folders.length === 0 ? (
            <Muted>Inga mappar ännu.</Muted>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {folders.map((f) => (
                <FolderItem key={f}>
                  <div style={{ display: "grid", gap: 4 }}>
                    <FolderName>{f}</FolderName>
                    <Muted>
                      {selectedFolder === f
                        ? `Vald mapp • ${imagesOnly.length} bilder, ${videosOnly.length} videos`
                        : "Klicka för att visa"}
                    </Muted>
                  </div>

                  <Row style={{ marginTop: 0 }}>
                    <Ghost type="button" onClick={() => setSelectedFolder(f)} disabled={busy}>
                      Visa
                    </Ghost>
                    <Danger type="button" onClick={() => deleteFolder(f)} disabled={busy}>
                      Ta bort
                    </Danger>
                  </Row>
                </FolderItem>
              ))}
            </div>
          )}

          {selectedFolder ? (
            <>
              <div style={{ height: 14 }} />
              <Title>Innehåll: {selectedFolder}</Title>
              <div style={{ height: 10 }} />

              {items.length === 0 ? (
                <Muted>Inget i denna mapp.</Muted>
              ) : (
                <>
                  {imagesOnly.length ? (
                    <>
                      <Muted style={{ marginBottom: 8 }}>Bilder</Muted>
                      <ThumbGrid>
                        {imagesOnly.map((it) => (
                          <ThumbWrap key={it.id}>
                            <Thumb src={it.url || it.imageUrl} alt={it.title || "Bild"} />
                            <ThumbActions>
                              <Tiny>{it.title || "bild"}</Tiny>
                              <Danger type="button" onClick={() => deleteImage(it)} disabled={busy}>
                                Ta bort
                              </Danger>
                            </ThumbActions>
                          </ThumbWrap>
                        ))}
                      </ThumbGrid>
                    </>
                  ) : null}

                  {videosOnly.length ? (
                    <>
                      <div style={{ height: 14 }} />
                      <Muted style={{ marginBottom: 8 }}>Videos</Muted>
                      <div style={{ display: "grid", gap: 10 }}>
                        {videosOnly.map((it) => (
                          <FolderItem key={it.id}>
                            <div style={{ display: "grid", gap: 6 }}>
                              <FolderName>Video</FolderName>
                              <Muted>{it.youtubeLink}</Muted>
                            </div>
                            <Danger
                              type="button"
                              onClick={async () => {
                                if (!window.confirm("Ta bort video?")) return;
                                setBusy(true);
                                try {
                                  await deleteDoc(doc(firestore, "gallery", it.id));
                                  await loadFolderItems(selectedFolder);
                                } catch (e) {
                                  alert(e?.message || "Kunde inte ta bort.");
                                } finally {
                                  setBusy(false);
                                }
                              }}
                              disabled={busy}
                            >
                              Ta bort
                            </Danger>
                          </FolderItem>
                        ))}
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </Card>
      </Grid>
    </AdminShell>
  );
}

